import { LineReadable } from "./line-readable";
import { ReadonlyVec } from "./readonly";
import { Vec } from "./vec";
import { VecLike } from "./vec-like";

export class LineStringReadable<Point extends ReadonlyVec,Points extends ReadonlyArray<Point>>{
	public constructor(
		public points:Points,
	){
	}

	public get count(){
		return this.points.length;
	}

	public [Symbol.iterator](){
		return this.points.values();
	}

	public pairs(){
		return this.points.pairs();
	}

	public triplets(){
		return this.points.triplets();
	}

	public at(i:number){
		return this.points.at(i)!;
	}

	public atIndex(index:number, clamp:true):Point['vecType'];
	public atIndex(index:number, clamp:false):Point['vecType']|undefined;
	public atIndex(index:number, clamp:boolean){
		const {points}=this;
		if(clamp){
			if(index<0)
				index=0;
			if(index>points.length-1)
				index=points.length-1;
		}

		let i=Math.floor(index);
		if(i<0)
			return undefined;
		if(i>points.length-1)
			return undefined;
		const p=points[i].clone();
		if(i===index)
			return p;
		return p.lerp(points[i+1],index-i);
	}

	public len(){
		let length=0;
		for(const [a,b] of this.points.pairs())
			length+=a.distTo(b);
		return length;
	}

	public eq(that:LineStringReadable<ReadonlyVec,ReadonlyArray<Point>>){
		if(this.points.length!==that.points.length)
			return false;
		for(const [i,p] of this.points.entries()){
			if(!p.eq(that.points[i]))
				return false;
		}
		return true;
	}

	public vectorAt(i:number){
		if(i<0)
			i+=(this.points.length-1);
		const a=this.points[i];
		const b=this.points[i+1];
		if(!(a && b))
			return null;
		return b.delta(a).nrm();
	}

	public vectorAtClamp(i:number){
		const {points}=this;
		if(points.length<2)
			return null;
		if(i<0)
			i=0;
		if(i>points.length-2)
			i=points.length-2;
		const a=points[i];
		const b=points[i+1];
		return b.delta(a).nrm();
	}

	public indexAtDist(length:number){
		if(length<=0)
			return 0;
		for(const [a,b,i] of this.points.pairs()){
			const segLength=a.distTo(b);
			if(length<segLength){
				return i+length/segLength;
			}else{
				length-=segLength;
			}
		}
		return this.count-1;
	}

	public atDist(length:number, clamp:boolean):Point['vecType']{
		if(clamp && length<=0)
			return this.points[0].clone();
		for(const [a,b] of this.points.pairs()){
			const segLength=a.distTo(b);
			if(length<segLength){
				return a.clone().lerp(b,length/segLength);
			}else{
				length-=segLength;
			}
		}
		if(clamp)
			return this.points.at(-1)!.clone();
		const a=this.points.at(-2)!;
		const b=this.points.at(-1)!;
		const segLength=a.distTo(b);
		return a.clone().lerp(b,length/segLength);
	}

	public distTo(focus:Vec){
		return LineStringReadable.distTo(this.points,false,focus);
	}

	public closestPoint(focus:Vec){
		return LineStringReadable.closestPoint<Point>(this.points,false,focus);
	}

	public closestIndex(focus:Vec){
		return LineStringReadable.closestIndex(this.points,false,focus);
	}


}

export namespace LineStringReadable{
	const tmpLine=new LineReadable<ReadonlyVec>(<any>null,<any>null);

	export function distToSq(points:ReadonlyArray<ReadonlyVec>, loop:boolean, focus:VecLike){
		if(points.length===0)
			return undefined;
		let foundDistSq=points[0].distToSq(focus);
		const line=tmpLine;
		for([line.a,line.b] of points.pairs(loop)){
			const distSq=line.distToPointSq(focus);
			if(foundDistSq>distSq)
				foundDistSq=distSq;
		}
		return foundDistSq;
	}

	export function distTo(points:ReadonlyArray<ReadonlyVec>, loop:boolean, focus:VecLike){
		if(points.length===0)
			return undefined;
		return Math.sqrt(distToSq(points,loop,focus)!);
	}

	export function closestPoint<Point extends ReadonlyVec>(points:ReadonlyArray<Point>, loop:boolean, focus:VecLike):Point['cloneType']|null{
		if(points.length===0)
			return null;
		let found=points[0].clone();
		let foundDistSq=points[0].distToSq(focus);
		const line=<LineReadable<Point>>tmpLine;
		let i:number;
		for([line.a,line.b,i] of points.pairs(loop)){
			const c=line.closestPoint(focus);
			const distSq=c.distToSq(focus);
			if(foundDistSq>distSq){
				foundDistSq=distSq;
				found=c;
			}
		}
		return found;
	}

	export function closestIndex(points:ReadonlyArray<ReadonlyVec>, loop:boolean, focus:VecLike){
		if(points.length===0)
			return null;
		let found=0;
		let foundDistSq=points[0].distToSq(focus);
		const line=tmpLine;
		let i:number;
		for([line.a,line.b,i] of points.pairs(loop)){
			const c=line.closestPoint(focus);
			const distSq=c.distToSq(focus);
			if(foundDistSq>distSq){
				foundDistSq=distSq;
				found=i+line.indexOf(c);
			}
		}
		return found;
	}

	export function fragment<Point extends Vec>(points:Point[], loop:boolean, wantedInterval:number, forEachNewPoint?:(p:Point)=>void):Point[];
	export function fragment<Point extends ReadonlyVec>(points:Point[], loop:boolean, wantedInterval:number, forEachNewPoint?:(p:ReturnType<Point['clone']>)=>void):(Point|ReturnType<Point['clone']>)[];
	export function fragment(
		points:ReadonlyVec[],
		loop:boolean,
		wantedInterval:number,
		forEachNewPoint:(p:Vec)=>void=(()=>{}),
	){
		if(points.length<2 || wantedInterval<=0)
			return points.slice();
		const out=points.slice(0,1);
		for(const [a,b] of points.pairs(loop)){
			const dist=a.distTo(b);
			const count=Math.ceil(dist/wantedInterval);
			for(let j=1;j<count;j++){
				const c=a.clone().lerp(b,j/count);
				forEachNewPoint(c);
				out.push(c);
			}
			out.push(b);
		}
		if(loop)
			out.pop();
		return out;
	}

}
